import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Form,
  Row,
  Col,
  Table,
  Typography,
  DatePicker,
  InputNumber,
  Modal,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import CustomLayout from "../../Layout/CustomLayout";
import PharmacySideMenu from "../../Layout/CustomLayout/PharmacySideMenu";
import { UiButton, UiTag, ActionIconButton } from "../../ui";
import {
  ListScreen,
  DetailHead,
  DetailGrid,
  DetailFull,
  DetailCard,
  DetailBody,
  DetailEmpty,
  CardNote,
  FootTotal,
  BillTotals,
  AddLineButton,
  IconAction,
  RowActions,
  listTableClass,
} from "../common/ListScreen";
import CgIcon from "../common/CgIcon";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import GstSummaryTable from "../common/GstSummaryTable";
import { fetchActivePharmacyMedicines } from "../common/medicineSearch";
import { formatAmount } from "../common/money";
import { rateAboveMrpError } from "../common/validators";
import { getPharmacyMedicineApi } from "../../../services/pharmacyMedicines.services";
import { getPharmacyConfigListApi } from "../../../services/pharmacyConfigs.services";
import {
  getPharmacyPurchaseOrderApi,
  createPharmacyPurchaseOrderApi,
  editPharmacyPurchaseOrderApi,
  sendPharmacyPurchaseOrderApi,
  cancelPharmacyPurchaseOrderApi,
  printPharmacyPurchaseOrder,
  addPharmacyPurchaseOrderEditItemApi,
  updatePharmacyPurchaseOrderEditItemApi,
  removePharmacyPurchaseOrderEditItemApi,
} from "../../../services/pharmacyPurchaseOrders.services";
import { getErrorMessage } from "../../../helpers/error";
import PharmacyGlobalSearch from "../common/PharmacyGlobalSearch";
import AuditLogDrawer, { AUDIT_ENTITIES } from "../common/AuditLogDrawer";
import LeaveWithoutSavingModal from "../common/LeaveWithoutSavingModal";
import {
  canCancelDocument,
  canEditDocument,
  cancelFailureMessage,
  purchaseFailureMessage,
} from "../common/cancelWindow";
import { useIsPharmacyAdmin } from "../Users/PharmacyUsers";

const LIST_PATH = "/pharmacy/purchase-order";

/* Fully received means every line has had everything it asked for delivered;
   a line with nothing received yet reads as zero rather than as missing. */
const isFullyReceived = (order) => {
  const items = order?.items || [];
  if (!items.length) return false;
  return items.every(
    (item) => Number(item.received_qty || 0) >= Number(item.ordered_qty || 0)
  );
};

const footerActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: 12,
};

const fetchVendors = (text) =>
  getPharmacyConfigListApi(
    "vendors",
    new URLSearchParams({ search: text || "", limit: 50 })
  ).then((res) =>
    (res?.data?.results || []).map((item) => ({ value: item.id, label: item.name }))
  );

let rowSeq = 0;
const newRow = () => ({
  key: `po-${(rowSeq += 1)}`,
  medicine: undefined,
  medicine_name: "",
  ordered_qty: null,
  rate: null,
  discount: 0,
  gst_percent: 0,
  /* Filled from the catalogue when the product is chosen - the line has no MRP
     column of its own, and a rate has to be measured against something. */
  mrp: null,
});

/**
 * A line's money figures are derived from qty, rate, discount and GST rather
 * than entered, so the totals cannot disagree with the lines they came from.
 */
const deriveLine = (row) => {
  const qty = Number(row?.ordered_qty || 0);
  const rate = Number(row?.rate || 0);
  const gross = qty * rate;
  const discount = Number(row?.discount || 0);
  const taxable = Math.max(gross - discount, 0);
  const gstPercent = Number(row?.gst_percent || 0);
  // Intra-state: GST splits evenly between SGST and CGST.
  const half = (taxable * gstPercent) / 200;
  return {
    ...row,
    taxable_amount: Number(taxable.toFixed(2)),
    discount: Number(discount.toFixed(2)),
    sgst: Number(half.toFixed(2)),
    cgst: Number(half.toFixed(2)),
    igst: 0,
    amount: Number((taxable + half * 2).toFixed(2)),
  };
};

/* A saved line, in the shape the grid reads and types into. */
const itemToRow = (item, index) => ({
  key: `saved-${item.id ?? index}`,
  itemId: item.id,
  medicine: item.medicine,
  medicine_name: item.medicine_name || "",
  ordered_qty: Number(item.ordered_qty) || 0,
  received_qty: Number(item.received_qty) || 0,
  rate: Number(item.rate) || 0,
  discount: Number(item.discount) || 0,
  gst_percent:
    Number(item.taxable_amount) > 0
      ? Number(
          (
            ((Number(item.sgst) + Number(item.cgst)) * 100) /
            Number(item.taxable_amount)
          ).toFixed(2)
        )
      : 0,
  taxable_amount: Number(item.taxable_amount) || 0,
  sgst: Number(item.sgst) || 0,
  cgst: Number(item.cgst) || 0,
  igst: Number(item.igst) || 0,
  amount: Number(item.amount) || 0,
  mrp: null,
});

const lineFigures = (row) =>
  row.itemId && !row.touched
    ? {
        ...row,
        taxable_amount: Number(row.taxable_amount) || 0,
        sgst: Number(row.sgst) || 0,
        cgst: Number(row.cgst) || 0,
        igst: Number(row.igst) || 0,
        amount: Number(row.amount) || 0,
      }
    : deriveLine(row);

const PurchaseOrderPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !orderId || orderId === "new";


  const prefill = isNew ? location.state?.purchaseOrderPrefill : null;

  const [form] = Form.useForm();
  const [order, setOrder] = useState(null);
  const [printing, setPrinting] = useState(false);
  /*
   * An order opens with no lines, the way a purchase entry does - the empty
   * state offers the first one rather than a blank row sitting there asking to
   * be filled in.
   */
  const [rows, setRows] = useState(() => prefill?.rows || []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  /* Leaving with an order that has not been raised yet asks first. */
  const [leaveOpen, setLeaveOpen] = useState(false);
  /* What has happened to this order, read beside it - Pharmacy Admins only,
     which is who the endpoint answers. */
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (isNew) return;
    /* The document in hand belongs to the last id this screen was opened on.
       One route serves them all, so moving from one to another changes the
       parameter without remounting - and the previous one sits in state, whole
       and wrong, until the new one lands. A refresh of the same id is not that
       and must not blank the screen. */
    setOrder((prev) => (String(prev?.id) === String(orderId) ? prev : null));
    setLoading(true);
    getPharmacyPurchaseOrderApi(orderId)
      .then((res) => {
        const data = res?.data;
        setOrder(data);
        form.setFieldsValue({
          vendor: data?.vendor,
          order_date: data?.order_date ? dayjs(data.order_date) : null,
          expected_delivery_date: data?.expected_delivery_date
            ? dayjs(data.expected_delivery_date)
            : null,
        });
        /* The order's own lines are the table's rows from here - read as they
           stand, and typed into one at a time when one is being edited. */
        setRows((data?.items || []).map(itemToRow));
      })
      .catch((err) => message.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [orderId, isNew, form]);

  useEffect(() => {
    if (prefill?.vendor) form.setFieldValue("vendor", prefill.vendor);
  }, [prefill?.vendor, form]);

  const isPharmacyAdmin = useIsPharmacyAdmin();
  /*
   * Editing is a line at a time rather than the whole order at once: the
   * pencil at the end of a line opens that line and nothing else, which is how
   * an order is actually corrected - one rate, one quantity. The header above
   * is a matter of record once the order is raised.
   *
   * `editingKey` is the line being typed into, or null when none is.
   */
  const [editingKey, setEditingKey] = useState(null);
  /* The line as it was when it was opened, so the cross can put it back
     without disturbing anything else that has been typed. */
  const [rowBackup, setRowBackup] = useState(null);
  /*
   * Lines taken off the table that the server still holds.
   *
   * Taking a line off the grid is not telling the server about it - the
   * footer does that - so the ids are kept here until it is pressed, and each
   * one is withdrawn by name rather than by being left out of a list.
   */
  const [removedItemIds, setRemovedItemIds] = useState([]);
  /* Whether the table says something the server has not been told. Nothing
     goes up until the footer is pressed, so this is what the footer is for. */
  const [dirty, setDirty] = useState(false);
  const readOnly = Boolean(order?.id);
  /*
   * What may still be rewritten, on the same rule that governs calling it off:
   * a draft any time, a completed one inside the day it was submitted, and a
   * Pharmacy Admin whenever. A cancelled one, never.
   */
  const linesEditable =
    isNew || canEditDocument(order, { isAdmin: isPharmacyAdmin });
  const isEditingRow = (row) => isNew || editingKey === row.key;

  const startRowEdit = (row) => {
    setRowBackup({ key: row.key, row });
    setEditingKey(row.key);
  };

  /* The tick: what was typed stays on the table, and the line closes. It is
     still only in the browser - the footer is what writes it. */
  const commitRowEdit = () => {
    setRowBackup(null);
    setEditingKey(null);
  };

  /* The cross: this line goes back to what it was, and nothing else moves. A
     line that was added and never filled in goes entirely. */
  const revertRowEdit = () => {
    if (rowBackup) {
      setRows((prev) =>
        prev.map((r) => (r.key === rowBackup.key ? rowBackup.row : r))
      );
    } else if (editingKey) {
      setRows((prev) => prev.filter((r) => r.key !== editingKey));
    }
    setRowBackup(null);
    setEditingKey(null);
  };

  /* Everything back to what the server holds. */
  const discardChanges = () => {
    setRows((order?.items || []).map(itemToRow));
    setRemovedItemIds([]);
    setRowBackup(null);
    setEditingKey(null);
    setDirty(false);
  };

  const lines = useMemo(
    () => rows.filter((r) => r.medicine).map(lineFigures),
    [rows]
  );

  const totals = useMemo(() => {
    const taxable = lines.reduce((s, l) => s + Number(l.taxable_amount || 0), 0);
    const discount = lines.reduce((s, l) => s + Number(l.discount || 0), 0);
    const gst = lines.reduce(
      (s, l) => s + Number(l.sgst || 0) + Number(l.cgst || 0) + Number(l.igst || 0),
      0
    );
    return {
      subtotal: taxable + discount,
      discount,
      taxable,
      gst,
      grandTotal: taxable + gst,
    };
  }, [lines]);

  const patchRow = (key, patch) => {
    setDirty(true);
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch, touched: true } : r))
    );
  };

  /*
   * Choosing a product brings its MRP down with it, so the rate beside it can be
   * checked. The lookup is best effort: a line whose MRP never arrives is left
   * unmeasured rather than blocked.
   */
  const chooseMedicine = (key, value, option) => {
    patchRow(key, {
      medicine: value,
      medicine_name: option?.label || "",
      mrp: null,
    });
    if (!value) return;
    getPharmacyMedicineApi(value)
      .then((res) => patchRow(key, { mrp: Number(res?.data?.mrp) || null }))
      .catch(() => {});
  };

  const rowRateError = (row) => rateAboveMrpError(row?.rate, row?.mrp, "Rate");

  /* One line, in the shape every write takes it - whole-order or single. */
  const itemPayload = (l) => ({
    medicine: l.medicine,
    ordered_qty: Number(l.ordered_qty || 0),
    rate: Number(l.rate || 0),
    discount: Number(l.discount || 0),
    taxable_amount: Number(l.taxable_amount || 0),
    sgst: Number(l.sgst || 0),
    cgst: Number(l.cgst || 0),
    igst: 0,
    amount: Number(l.amount || 0),
  });

  /* `leaveAfter` is set by the leave dialog's Save, which wants the list once
     the order is written rather than the order it just saved. */
  const handleSave = async (leaveAfter = false) => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!lines.length) {
      message.error("Add at least one product.");
      return;
    }
    /* Named, not just counted - on a long order the pharmacist has to know
       which line to go back to. */
    const overMrp = rows.filter((row) => rowRateError(row));
    if (overMrp.length) {
      message.error(
        overMrp.length === 1
          ? `${overMrp[0].medicine_name || "A product"}: ${rowRateError(overMrp[0])}`
          : `${overMrp.length} lines have a rate above their MRP. Correct them before saving.`
      );
      return;
    }

    setSaving(true);

    /* The header, in the shape both the create and the whole-order edit take. */
    const header = {
      vendor: values.vendor,
      order_date: values.order_date.format("YYYY-MM-DD"),
      expected_delivery_date: values.expected_delivery_date
        ? values.expected_delivery_date.format("YYYY-MM-DD")
        : undefined,
      subtotal: Number(totals.subtotal.toFixed(2)),
      discount: Number(totals.discount.toFixed(2)),
      gst_amount: Number(totals.gst.toFixed(2)),
      total_amount: Number(totals.grandTotal.toFixed(2)),
    };

    /*
     * Whether anything above the table was retyped.
     *
     * It decides how the order is written: the header and the lines go up
     * together or they do not go up together, and only the first needs the
     * whole list sent.
     */
    const headerChanged =
      String(values.vendor ?? "") !== String(order?.vendor ?? "") ||
      header.order_date !== (order?.order_date || null) ||
      (header.expected_delivery_date || null) !==
        (order?.expected_delivery_date || null);

    try {
      if (!order?.id) {
        const res = await createPharmacyPurchaseOrderApi({
          ...header,
          items: lines.map(itemPayload),
        });
        const number = res?.data?.order_number;
        message.success(
          number ? `Purchase order ${number} created.` : "Purchase order created."
        );
        navigate(LIST_PATH);
        return;
      }

      let saved;
      if (headerChanged) {
        /*
         * The header moved, so the whole order goes up at once - which is
         * what `edit/` is for, and it takes the lines with it. Anything left
         * out of that list is withdrawn by the server, so the list is the
         * table as it stands and the withdrawals are already in it.
         */
        const res = await editPharmacyPurchaseOrderApi(order.id, {
          ...header,
          items: lines.map(itemPayload),
        });
        saved = res?.data;
        setRemovedItemIds([]);
      } else {
        /*
         * Only the table moved, so only the lines that moved are sent - one
         * call each, naming the line. A pharmacist who corrected one rate on
         * a twenty-line order has the server told about that rate and nothing
         * else, and the nineteen lines nobody touched are not rewritten.
         */
        for (const itemId of removedItemIds) {
          // eslint-disable-next-line no-await-in-loop
          await removePharmacyPurchaseOrderEditItemApi(order.id, itemId);
        }
        setRemovedItemIds([]);

        for (const line of lines) {
          if (!line.itemId) {
            // eslint-disable-next-line no-await-in-loop
            await addPharmacyPurchaseOrderEditItemApi(order.id, itemPayload(line));
          } else if (line.touched) {
            // eslint-disable-next-line no-await-in-loop
            await updatePharmacyPurchaseOrderEditItemApi(
              order.id,
              line.itemId,
              itemPayload(line)
            );
          }
        }

        /* The server recomputes the order's own figures as each line lands,
           so what it now holds is read back rather than guessed at. */
        const fresh = await getPharmacyPurchaseOrderApi(order.id);
        saved = fresh?.data;
      }

      /* An edit stays on the order it changed - there is usually more to
         check once it is saved - unless it was saved on the way out. */
      const current = saved || order;
      setOrder(current);
      setRows((current?.items || []).map(itemToRow));
      setEditingKey(null);
      setRowBackup(null);
      setDirty(false);
      message.success(
        current?.order_number
          ? `Purchase order ${current.order_number} saved.`
          : "Changes saved."
      );
      if (leaveAfter) navigate(LIST_PATH);
    } catch (err) {
      message.error(purchaseFailureMessage(err, getErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  /*
   * Leaving.
   *
   * An order is written in one call when it is raised, so until then every
   * line and every field on the header is held in the browser and would go
   * with the screen. A saved order is read-only and has nothing to lose, so
   * going back from one is let through without a word.
   */
  /* Read when the question is asked rather than on every render - the form's
     own fields do not re-render this screen as they are filled in. */
  const hasUnsavedWork = () =>
    dirty || (!readOnly && (rows.length > 0 || Boolean(form.getFieldValue("vendor"))));

  const leaveScreen = () => {
    if (!hasUnsavedWork()) {
      navigate(LIST_PATH);
      return;
    }
    setLeaveOpen(true);
  };

  const saveAndLeave = async () => {
    /* A new order lands on the list as soon as it is raised; an edit is told
       to follow it there. A refusal leaves the reason on screen. */
    setLeaveOpen(false);
    await handleSave(true);
  };

  /*
   * Calling the order off.
   *
   * A draft can go at any time. A completed order can be called off inside the
   * day it was submitted and no longer, which a Pharmacy Admin is not held to.
   * An order the vendor has already delivered in full is finished with either
   * way - the endpoint refuses it, and so does the screen. Asked about first:
   * a cancelled order cannot be reopened.
   */
  const canCancel =
    canCancelDocument(order, { isAdmin: isPharmacyAdmin }) &&
    !isFullyReceived(order);

  const handleCancelOrder = () => {
    Modal.confirm({
      title: "Cancel this purchase order?",
      content: "Are you sure? This cannot be undone.",
      okText: "Yes, cancel it",
      okButtonProps: { danger: true },
      cancelText: "No",
      /* Cancelling is done with the order, so the screen goes back to the
         list rather than sitting on a document nobody can do anything with. */
      onOk: () =>
        cancelPharmacyPurchaseOrderApi(orderId)
          .then(() => {
            message.success("Order cancelled.");
            navigate(LIST_PATH);
          })
          .catch((err) =>
            message.error(cancelFailureMessage(err, getErrorMessage(err)))
          ),
    });
  };

  const handlePrint = () => {
    setPrinting(true);
    printPharmacyPurchaseOrder(orderId)
      .catch((err) =>
        message.error(err?.message || getErrorMessage(err))
      )
      .finally(() => setPrinting(false));
  };

  const runAction = (fn, done) => {
    setSaving(true);
    fn(orderId)
      .then((res) => {
        message.success(done);
        setOrder(res?.data || order);
      })
      .catch((err) => message.error(getErrorMessage(err)))
      .finally(() => setSaving(false));
  };

  /*
   * One set of columns, read or typed a line at a time.
   *
   * The line being edited shows its controls; every other line shows its
   * figures. A new order is every line at once - there is nothing saved to
   * protect - and a saved one is corrected from the pencil at the end of the
   * line that is wrong.
   */
  const columns = [
    {
      title: "Product",
      key: "medicine",
      width: "26%",
      render: (_, row) =>
        isEditingRow(row) ? (
          <AsyncSearchSelect
            fetcher={fetchActivePharmacyMedicines}
            placeholder="Search product"
            value={row.medicine}
            initialOption={
              row.medicine
                ? { value: row.medicine, label: row.medicine_name }
                : undefined
            }
            onChange={(value, option) => chooseMedicine(row.key, value, option)}
          />
        ) : (
          row.medicine_name || "-"
        ),
    },
    {
      title: "Qty",
      key: "ordered_qty",
      align: "right",
      width: "11%",
      render: (_, row) =>
        isEditingRow(row) ? (
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            value={row.ordered_qty}
            onChange={(value) => patchRow(row.key, { ordered_qty: value })}
          />
        ) : (
          row.ordered_qty
        ),
    },
    /* Nothing has been received against an order that has not been raised. */
    ...(isNew
      ? []
      : [
          {
            title: "Received",
            dataIndex: "received_qty",
            key: "received_qty",
            align: "right",
            width: "10%",
          },
        ]),
    {
      title: "Rate",
      key: "rate",
      align: "right",
      width: "13%",
      render: (_, row) => {
        if (!isEditingRow(row)) return formatAmount(row.rate || 0);
        const error = rowRateError(row);
        return (
          <>
            <InputNumber
              min={0}
              precision={2}
              status={error ? "error" : undefined}
              style={{ width: "100%" }}
              value={row.rate}
              onChange={(value) => patchRow(row.key, { rate: value })}
            />
            {error ? (
              <Typography.Text
                type="danger"
                style={{ display: "block", fontSize: 11, lineHeight: 1.35, marginTop: 4 }}
              >
                {error}
              </Typography.Text>
            ) : null}
          </>
        );
      },
    },
    {
      title: "Discount",
      key: "discount",
      align: "right",
      width: "12%",
      render: (_, row) =>
        isEditingRow(row) ? (
          <InputNumber
            min={0}
            precision={2}
            style={{ width: "100%" }}
            value={row.discount}
            onChange={(value) => patchRow(row.key, { discount: value })}
          />
        ) : (
          formatAmount(row.discount || 0)
        ),
    },
    {
      title: "GST %",
      key: "gst_percent",
      align: "right",
      width: "11%",
      render: (_, row) =>
        isEditingRow(row) ? (
          <InputNumber
            min={0}
            max={100}
            style={{ width: "100%" }}
            value={row.gst_percent}
            onChange={(value) => patchRow(row.key, { gst_percent: value })}
          />
        ) : (
          `${row.gst_percent || 0}%`
        ),
    },
    {
      title: "Taxable",
      key: "taxable_amount",
      align: "right",
      width: "12%",
      render: (_, row) => formatAmount(lineFigures(row).taxable_amount),
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      width: "12%",
      render: (_, row) => formatAmount(lineFigures(row).amount),
    },
    {
      title: "",
      key: "actions",
      width: "10%",
      render: (_, row) => {
        if (!linesEditable) return null;

        /* A new order is typed line by line as it is built, so its lines
           only need taking off. A saved one is corrected a line at a time:
           the pencil opens one, the tick keeps what was typed, the cross puts
           it back. None of the three touches the server. */
        if (isNew) {
          return (
            <RowActions>
              <IconAction
                icon="trash"
                label="Remove line"
                tone="danger"
                onClick={() => removeRow(row.key)}
              />
            </RowActions>
          );
        }

        return (
          <RowActions>
            {editingKey === row.key ? (
              <>
                <IconAction
                  icon="check"
                  label="Keep these changes"
                  onClick={commitRowEdit}
                />
                <IconAction
                  icon="close"
                  label="Undo this line"
                  tone="danger"
                  onClick={revertRowEdit}
                />
              </>
            ) : (
              <>
                <IconAction
                  icon="edit"
                  label={`Edit ${row.medicine_name || "line"}`}
                  disabled={editingKey !== null}
                  onClick={() => startRowEdit(row)}
                />
                <IconAction
                  icon="trash"
                  label="Remove line"
                  tone="danger"
                  disabled={editingKey !== null}
                  onClick={() => removeRow(row.key)}
                />
              </>
            )}
          </RowActions>
        );
      },
    },
  ];

  /* A line added to a saved order opens for typing straight away - it has
     nothing in it, and nothing to read. */
  /*
   * Whether the bar at the foot has anything in it.
   *
   * It used to render before the order had arrived, so opening a cancelled
   * order - or a completed one past its window - flashed up a bar of actions
   * for a second and then took it away again. Nothing is offered until the
   * order is known, and an order with nothing left to do with it gets no bar
   * at all rather than one holding a lone way out the back link already is.
   */
  const orderKnown = isNew || String(order?.id) === String(orderId);
  const showFooter =
    orderKnown && (isNew || dirty || order?.status === "draft" || canCancel);

  const addRow = () => {
    const row = newRow();
    setRows((prev) => [...prev, row]);
    /* No backup: a line that was just added has nothing to go back to, so the
       cross takes it off again. */
    setRowBackup(null);
    setEditingKey(row.key);
    setDirty(true);
  };

  const removeRow = (key) => {
    setRows((prev) => {
      const going = prev.find((r) => r.key === key);
      if (going?.itemId) {
        setRemovedItemIds((ids) =>
          ids.includes(going.itemId) ? ids : [...ids, going.itemId]
        );
      }
      return prev.filter((r) => r.key !== key);
    });
    if (editingKey === key) {
      setRowBackup(null);
      setEditingKey(null);
    }
    setDirty(true);
  };
  const lineCount = rows.length;

  return (
    <CustomLayout
      headerSearch={<PharmacyGlobalSearch />}
      layoutClassName="reception-layout compact-footer-layout pharmacy-motion"
      sideContent={<PharmacySideMenu />}
      header={
        // The mockup's header carries only search and the account menu; each
        // screen names itself in its own heading. Kept truthy so CustomLayout
        // still renders the bar.
        <></>
      }
      footer={
        !showFooter ? undefined : (
        <div style={footerActionsStyle}>
          {/*
            * Cancel calls the order off where there is an order to call off,
            * and is the way out before that - an order that was never raised
            * has nothing on the server to cancel. While it is being edited it
            * is the way out of the edit, which Discard changes already is, so
            * it stays a way out there too.
            */}
          <UiButton
            variant="danger"
            onClick={canCancel && !dirty ? handleCancelOrder : leaveScreen}
          >
            Cancel
          </UiButton>
          {isNew ? (
            <UiButton loading={saving} onClick={() => handleSave()}>
              Create Purchase Order
            </UiButton>
          ) : dirty ? (
            /*
             * Nothing typed into the table has gone anywhere yet - the tick on
             * a line only closes it. This is where the order is written, in
             * one call, or put back to what the server holds.
             */
            <>
              <UiButton variant="secondary" disabled={saving} onClick={discardChanges}>
                Discard changes
              </UiButton>
              <UiButton
                loading={saving}
                disabled={editingKey !== null}
                onClick={() => handleSave()}
              >
                Save changes
              </UiButton>
            </>
          ) : (
            <>
              {order?.status === "draft" && (
                <UiButton
                  loading={saving}
                  onClick={() =>
                    runAction(sendPharmacyPurchaseOrderApi, "Order marked as sent.")
                  }
                >
                  Mark as Sent
                </UiButton>
              )}
            </>
          )}
        </div>
        )
      }
    >
      <div
        style={{
          padding: 24,
          paddingTop: 0,
          paddingBottom: showFooter ? 120 : 24,
        }}
      >
        <ListScreen>
          <DetailHead
            backLabel="Back to Purchase Orders"
            onBack={leaveScreen}
            title={order?.order_number || "Raise Purchase Order"}
            meta={
              order?.status ? (
                <UiTag tone={order.status === "cancelled" ? "warning" : "success"} size="sm">
                  {order.status.replaceAll("_", " ")}
                </UiTag>
              ) : null
            }
            /* Print belongs with the order it prints, not down with the
               actions that change its status. */
            actions={
              !isNew && order?.id ? (
                <>
                  {isPharmacyAdmin ? (
                    <UiButton variant="secondary" onClick={() => setHistoryOpen(true)}>
                      <CgIcon name="ledger" size={16} stroke={2} />
                      Audit log
                    </UiButton>
                  ) : null}
                  <UiButton variant="secondary" loading={printing} onClick={handlePrint}>
                    <PrinterOutlined /> Print
                  </UiButton>
                </>
              ) : null
            }
          />

          <DetailGrid>
            <DetailFull>
              <DetailCard title="Order Header">
                <DetailBody>
                  <Form layout="vertical" form={form} disabled={readOnly}>
                    {/*
                      * What an order is raised against: the vendor it goes to,
                      * the day it was raised, and the day the goods are due.
                      */}
                    <Row gutter={[14, 8]}>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item
                          label="Vendor"
                          name="vendor"
                          rules={[{ required: true, message: "Please select vendor" }]}
                        >
                          <AsyncSearchSelect
                            fetcher={fetchVendors}
                            loadOnOpen
                            placeholder="Search vendor"
                            initialOption={
                              order?.vendor
                                ? { value: order.vendor, label: order.vendor_name }
                                : prefill?.vendor
                                ? { value: prefill.vendor, label: prefill.vendorName }
                                : undefined
                            }
                            onChange={(value) => form.setFieldValue("vendor", value)}
                            value={Form.useWatch("vendor", form)}
                            disabled={readOnly}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item
                          label="Order Date"
                          name="order_date"
                          initialValue={dayjs()}
                          rules={[{ required: true, message: "Please select the order date" }]}
                        >
                          <DatePicker
                            style={{ width: "100%" }}
                            format="DD MMM, YYYY"
                            disabled={readOnly}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item label="Expected Delivery Date" name="expected_delivery_date">
                          <DatePicker
                            style={{ width: "100%" }}
                            format="DD MMM, YYYY"
                            disabled={readOnly}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </DetailBody>
              </DetailCard>
            </DetailFull>

            <DetailFull>
              <DetailCard
                title="Items"
                count={lineCount}
                /* Nothing to total and nothing to add to until the first
                   product is picked - the empty state above offers that. */
                foot={
                  lineCount > 0 ? (
                    <FootTotal label="Order total" value={formatAmount(totals.grandTotal)}>
                      {linesEditable ? (
                        <AddLineButton onClick={addRow}>Add Item</AddLineButton>
                      ) : null}
                    </FootTotal>
                  ) : null
                }
                extra={
                  linesEditable ? (
                    <UiButton onClick={addRow}>
                      <PlusOutlined /> Add Item
                    </UiButton>
                  ) : null
                }
              >
                {lineCount === 0 ? (
                  <DetailEmpty
                    title="No products added"
                    hint={'Click "Add Item" to start a line and pick the product on it.'}
                  >
                    {linesEditable ? (
                      <AddLineButton onClick={addRow}>Add Item</AddLineButton>
                    ) : null}
                  </DetailEmpty>
                ) : (
                  <Table
                    className={listTableClass}
                    loading={loading}
                    rowKey={(row) => row.key || row.id}
                    columns={columns}
                    dataSource={rows}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                  />
                )}
              </DetailCard>
            </DetailFull>

            <DetailCard title="GST Summary">
              <DetailBody>
                <GstSummaryTable items={lines} />
              </DetailBody>
            </DetailCard>

            <DetailCard title="Bill">
              <DetailBody>
                <BillTotals
                  rows={[
                    { label: "Subtotal", value: formatAmount(totals.subtotal) },
                    { label: "Discount", value: formatAmount(totals.discount) },
                    { label: "GST", value: formatAmount(totals.gst) },
                  ]}
                  totalLabel="Grand Total"
                  totalValue={formatAmount(totals.grandTotal)}
                />
                <CardNote>
                  A purchase order does not change stock. Stock increases only
                  when the matching purchase entry is completed.
                </CardNote>
              </DetailBody>
            </DetailCard>
          </DetailGrid>
        </ListScreen>
      </div>

      <AuditLogDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        entityType={AUDIT_ENTITIES.purchaseOrder}
        entityId={order?.id}
        documentNumber={order?.order_number}
      />

      <LeaveWithoutSavingModal
        open={leaveOpen}
        saving={saving}
        title={
          order?.id ? "Leave without saving?" : "Leave without raising this order?"
        }
        heading={
          order?.id
            ? "These changes have not been saved"
            : "This order has not been raised"
        }
        detail={
          <>
            {rows.length} {rows.length === 1 ? "line" : "lines"} and the header
            above will be lost.{" "}
            {order?.id
              ? "Saving writes them over the order as it stands."
              : "Raising the order writes it and leaves it as a draft; stock only moves when the matching purchase entry is completed."}
          </>
        }
        saveLabel={order?.id ? "Save changes" : "Raise order"}
        onKeepEditing={() => setLeaveOpen(false)}
        onSave={saveAndLeave}
        onDiscard={() => navigate(LIST_PATH)}
      />
    </CustomLayout>
  );
};

export default PurchaseOrderPage;
